import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categoryData, categorySubTags } from '@/mocks/categories';
import { articleData } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSubTag, setActiveSubTag] = useState<string | null>(null);
  const category = slug ? categoryData[slug as keyof typeof categoryData] : null;
  const subTags = slug ? categorySubTags[slug as keyof typeof categorySubTags] || [] : [];

  const validArticles = Object.values(articleData).filter(
    (a) => a.categorySlug === slug
  );

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 51爆料网 | 实时追踪最新${category.name}内幕`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', category.description);
      }
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', 'http://heiliaobao.cn/category/all');
      }
    }
  }, [category, slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} - 51爆料网`,
          description: category.description,
          url: 'http://heiliaobao.cn/category/all',
          isPartOf: {
            '@type': 'WebSite',
            name: '51爆料网',
            url: 'http://heiliaobao.cn/',
          },
        })}
      </script>
      <main>
        <Navbar />

        <section className="relative w-full pt-16">
          <div className="relative w-full overflow-hidden rounded-b-[2.5rem]">
            <div className="absolute inset-0">
              <img
                src={category.bannerImage}
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            </div>

            <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="pt-4">
                <Breadcrumb
                  items={[
                    { name: '首页', path: '/' },
                    { name: category.name },
                  ]}
                />
              </div>
              <div className="flex flex-col lg:flex-row items-start gap-8 py-14 md:py-20">
                <div className="lg:w-1/2">
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-background-50 leading-[1.05] tracking-tight">
                    {category.name}
                  </h1>
                </div>
                <div className="lg:w-1/2 flex flex-col justify-end pt-2 lg:pt-6">
                  <p className="text-background-100/80 text-base md:text-lg leading-relaxed mb-4 max-w-md">
                    {category.description}
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-background-50 text-sm font-medium hover:text-primary-300 transition-colors cursor-pointer group"
                  >
                    返回首页
                    <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {subTags.length > 0 && (
          <section className="w-full py-10 md:py-14 bg-background-50">
            <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <span className="inline-block px-3 py-1 border border-primary-300 text-primary-600 text-xs font-semibold rounded-full">
                  快速筛选
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mt-3">
                  该类目热门追踪
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <button
                  onClick={() => setActiveSubTag(null)}
                  className={`group flex flex-col items-center p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                    activeSubTag === null
                      ? 'bg-primary-500 border-primary-500'
                      : 'bg-background-50 border-background-200/70 hover:bg-primary-500 hover:border-primary-500'
                  }`}
                >
                  <span className={`w-10 h-10 flex items-center justify-center rounded-lg mb-2 transition-all duration-300 ${
                    activeSubTag === null
                      ? 'bg-background-50/20 text-background-50'
                      : 'bg-background-200/70 text-foreground-600 group-hover:bg-background-50/20 group-hover:text-background-50'
                  }`}>
                    <i className="ri-apps-line text-lg"></i>
                  </span>
                  <span className={`text-xs font-medium transition-colors whitespace-nowrap ${
                    activeSubTag === null ? 'text-background-50' : 'text-foreground-700 group-hover:text-background-50'
                  }`}>
                    全部
                  </span>
                </button>
                {subTags.map((tag) => (
                  <button
                    key={tag.slug}
                    onClick={() => setActiveSubTag(tag.slug === activeSubTag ? null : tag.slug)}
                    className={`group flex flex-col items-center p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                      activeSubTag === tag.slug
                        ? 'bg-primary-500 border-primary-500'
                        : 'bg-background-50 border-background-200/70 hover:bg-primary-500 hover:border-primary-500'
                    }`}
                  >
                    <span className={`w-10 h-10 flex items-center justify-center rounded-lg mb-2 transition-all duration-300 ${
                      activeSubTag === tag.slug
                        ? 'bg-background-50/20 text-background-50'
                        : 'bg-background-200/70 text-foreground-600 group-hover:bg-background-50/20 group-hover:text-background-50'
                    }`}>
                      <i className={`${tag.icon} text-lg`}></i>
                    </span>
                    <span className={`text-xs font-medium transition-colors whitespace-nowrap ${
                      activeSubTag === tag.slug ? 'text-background-50' : 'text-foreground-700 group-hover:text-background-50'
                    }`}>
                      {tag.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
              <div>
                <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider font-heading">
                  最新爆料
                </span>
                <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mt-2">
                  {category.name}
                  <span className="block italic font-serif text-primary-500">实时追踪</span>
                </h2>
              </div>
              {activeSubTag && (
                <button
                  onClick={() => setActiveSubTag(null)}
                  className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </span>
                  清除筛选
                </button>
              )}
            </div>

            {validArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {validArticles.map((article, index) => {
                  const bgColors = ['bg-primary-100/60', 'bg-accent-100/60', 'bg-secondary-100/60', 'bg-primary-100/50'];
                  return (
                    <Link
                      key={article.id}
                      to="/category/all"
                      className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className={`${bgColors[index % 4]} rounded-2xl overflow-hidden`}>
                        <div className="relative h-[220px] md:h-[260px] overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4 bg-background-50">
                          <span className="text-xs text-primary-500 font-medium">{article.category}</span>
                          <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 leading-snug mt-1 group-hover:text-primary-500 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-2">
                            {new Date(article.publishedAt).toLocaleDateString('zh-CN', {
                              month: 'short',
                              day: 'numeric',
                            })}
                            {' · '}
                            {article.views ? `${(article.views / 10000).toFixed(0)}万阅读` : ''}
                          </p>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200/70 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </span>
                <p className="text-foreground-500 text-sm">该分类暂无爆料文章</p>
                <Link to="/" className="text-primary-500 text-sm mt-2 inline-block hover:underline">返回首页查看更多</Link>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}